// Session storage for the signed-in user.
//
// The app reads the signed-in user from this small record in browser storage (key
// nell:v1:session): { user:{id,name,role,email?}, provider, issuedAt, expiresAt }.
// With the mock provider it IS the session. With Supabase it mirrors the real Supabase
// session (whose tokens supabase-js keeps itself, see src/auth/supabaseClient.js) and adds
// the RouteZero expiry: 12 h, or 30 days with "Keep me signed in".
import { store } from '../core/store.js';

const KEY = 'session';
const DEFAULT_TTL_MS = 12 * 60 * 60 * 1000; // 12 h
export const REMEMBER_TTL_MS = 30 * 24 * 60 * 60 * 1000; // "Keep me signed in": 30 days

/** @typedef {{ id: string, name: string, role: 'learner'|'admin' }} User */
/** @typedef {{ user: User, token: string, provider: string, issuedAt: number, expiresAt: number }} Session */

export function createSessionStore(st = store) {
  return {
    /** @returns {Session|null} */
    get() {
      const s = st.get(KEY);
      if (!s || !s.user || !s.token) return null;
      if (s.expiresAt && Date.now() > s.expiresAt) { st.remove(KEY); return null; }
      return s;
    },
    /** @param {Omit<Session,'issuedAt'|'expiresAt'> & {ttlMs?: number}} s */
    set({ user, token, provider, ttlMs = DEFAULT_TTL_MS }) {
      const now = Date.now();
      const session = { user, token, provider, issuedAt: now, expiresAt: now + ttlMs };
      st.set(KEY, session);
      return session;
    },
    clear() { st.remove(KEY); },
    /** The stored session even if it has expired (so the provider's own session can be ended too). */
    peek() { const s = st.get(KEY); return s && s.user ? s : null; },
    /** Replace the user (e.g. role or name re-read from the account database), keeping the expiry. */
    updateUser(user) { const s = st.get(KEY); if (s && s.user) { s.user = user; st.set(KEY, s); } return s; },
  };
}
export const ROLES = Object.freeze({ LEARNER: 'learner', ADMIN: 'admin' });
